import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { cx } from "@admin-ui/utils/cx";
import { formatDate } from "@admin-ui/utils/format-date";
import type AdminDestinationsFactorsIndex from "@travily/types/pages/AdminDestinationsFactorsIndex";
import { TableBody } from "react-aria-components";

export type FactorHealth = AdminDestinationsFactorsIndex["factors"][number];

export interface IndexViewProps {
  corpusSize: number;
  factors: FactorHealth[];
  measuredShare: number | null;
}

const PERCENT = 100;

function isRanked(factor: FactorHealth) {
  return factor.weight !== null;
}

// The manifest keeps the tiers as non-overlapping weight bands, so ordering by
// weight IS ordering by tier — decisive factors first, tiebreaks last. Reading
// the order off the weight rather than off a tier column means the two can
// never disagree.
function byWeight(a: FactorHealth, b: FactorHealth) {
  return (b.weight ?? 0) - (a.weight ?? 0);
}

// The row IS the weight: bar length is the factor's share of the ranking, and
// the filled part is how much of the corpus it could actually be scored on. A
// heavy factor measured on little is a long, mostly hollow bar, which is the
// one thing on this page that has to be visible without reading a number.
function WeightBar({ factor, heaviest }: { factor: FactorHealth; heaviest: number }) {
  const coverage = factor.coverage ?? 0;

  return (
    <div className="w-40" title={`${factor.weight} weight, ${Math.round(coverage * PERCENT)}% covered`}>
      <div
        className="h-2.5 overflow-hidden rounded-sm bg-quaternary"
        style={{ width: `${((factor.weight ?? 0) / heaviest) * PERCENT}%` }}
      >
        <div
          className={cx("h-full rounded-sm", coverage > 0 ? "bg-fg-brand-primary" : "bg-transparent")}
          style={{ width: `${coverage * PERCENT}%` }}
        />
      </div>
    </div>
  );
}

function ProducedAt({ factor }: { factor: FactorHealth }) {
  if (factor.kind !== "scheduled") return <span className="text-tertiary">Every reindex</span>;
  if (factor.last_produced_at === null) return <span className="font-medium text-error-primary">Never</span>;

  return (
    <span className={cx(factor.stale && "font-medium text-error-primary")}>{formatDate(factor.last_produced_at)}</span>
  );
}

// A pass that measures place by place can reach some and fail on others, which
// the date beside this cannot say — it is the luckiest place's — and which
// coverage cannot either, since a place with no playgrounds and a place the
// scan never reached both read as absent. Against the corpus, this separates
// them, and shows a sweep still working through the queue.
function MeasuredPlaces({ factor, corpusSize }: { factor: FactorHealth; corpusSize: number }) {
  if (factor.measured_places === null) return <span className="text-tertiary">—</span>;

  const behind = corpusSize - factor.measured_places;

  return (
    <span className={cx("tabular-nums", behind > 0 && "font-medium text-error-primary")}>
      {factor.measured_places}/{corpusSize}
    </span>
  );
}

// What the page is for, in one line. Anything short of the published total is
// weight the disclosure names and the ranking could not measure, and the rows
// below account for the difference.
function MeasuredShare({ corpusSize, factors, measuredShare }: IndexViewProps) {
  const ranked = factors.filter(isRanked);
  const published = ranked.reduce((total, factor) => total + (factor.weight ?? 0), 0);
  const dark = ranked.filter((factor) => factor.coverage === 0).length;
  const stale = factors.filter((factor) => factor.stale).length;
  const partial = ranked.filter((factor) => (factor.coverage ?? 0) > 0 && (factor.coverage ?? 0) < 1).length;

  if (measuredShare === null) {
    return (
      <p className="text-md text-tertiary">
        Nothing is indexed, so there is no corpus to measure coverage against. Reindex destinations first.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="font-semibold text-display-sm text-primary">
        {measuredShare.toFixed(1)} of {published.toFixed(1)}
      </p>
      <p className="text-md text-tertiary">published weight is measured, across {corpusSize} destinations.</p>
      <p className="text-sm text-tertiary">
        {dark} dark · {stale} stale · {partial} partly covered
      </p>
    </div>
  );
}

export function IndexView({ corpusSize, factors, measuredShare }: IndexViewProps) {
  const ranked = factors.filter(isRanked).sort(byWeight);
  const declared = factors.filter((factor) => !isRanked(factor));
  const heaviest = Math.max(...ranked.map((factor) => factor.weight ?? 0), 1);

  return (
    <>
      <div className="rounded-xl bg-secondary p-5 ring-1 ring-secondary ring-inset">
        <MeasuredShare corpusSize={corpusSize} factors={factors} measuredShare={measuredShare} />
      </div>

      <TableCard.Root size="sm">
        <Table aria-label="Ranking factors">
          <Table.Header>
            <Table.Head id="key" label="Factor" isRowHeader />
            <Table.Head id="weight" label="Weight and coverage" />
            <Table.Head id="coverage" label="" />
            <Table.Head id="source" label="Source" />
            <Table.Head id="job" label="Producer" />
            <Table.Head id="measured_places" label="Places measured" />
            <Table.Head id="last_produced_at" label="Last produced" />
          </Table.Header>
          <TableBody items={ranked}>
            {(factor: FactorHealth) => (
              <Table.Row key={factor.key} id={factor.key}>
                <Table.Cell className="font-medium">{factor.key}</Table.Cell>
                <Table.Cell>
                  <WeightBar factor={factor} heaviest={heaviest} />
                </Table.Cell>
                <Table.Cell className="tabular-nums">{Math.round((factor.coverage ?? 0) * PERCENT)}%</Table.Cell>
                <Table.Cell className="text-tertiary">{factor.source}</Table.Cell>
                <Table.Cell className="text-tertiary">{factor.job ?? "—"}</Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <MeasuredPlaces corpusSize={corpusSize} factor={factor} />
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <ProducedAt factor={factor} />
                </Table.Cell>
              </Table.Row>
            )}
          </TableBody>
        </Table>
      </TableCard.Root>

      {/* Declared as living design with no weight behind them yet, which is a
          different state from measured-on-nothing and reads as one in a table
          that shows both as empty bars. */}
      <div className="flex flex-col gap-3">
        <h2 className="font-semibold text-primary text-sm">Declared, not ranked</h2>
        <div className="flex flex-wrap gap-2">
          {declared.map((factor) => (
            <Badge key={factor.key} color="gray" size="sm">
              {factor.key} · {factor.source}
            </Badge>
          ))}
        </div>
      </div>
    </>
  );
}
